import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';

export function CommunicationPage(): Node {
  return html`
    <div>
      <h1>Communication</h1>
      <p>How host and remote microfrontends exchange data. The pattern depends on the isolation mode.</p>

      <h2>Host → Remote (Props)</h2>
      <p>Pass data down via the <code>props</code> option. The remote's default export receives them as its argument.</p>
      ${CodeBlock(`// Host
loadRemote({
  url: 'https://cdn.example.com/widgets/billing.js',
  props: {
    userId: currentUser().id,
    plan: 'pro',
    theme: 'dark',
  },
});

// Remote (billing.js)
export default function BillingWidget(props: {
  userId: string;
  plan: string;
  theme: string;
}): Node {
  return html\`<div class=\${props.theme}>Plan: \${props.plan}</div>\`;
}`)}

      <h2>Remote → Host (Callbacks)</h2>
      <p>Pass callback functions as props. The remote calls them to communicate back.</p>
      ${CodeBlock(`// Host
function App(): Node {
  const handleUpgrade = (newPlan: string) => {
    console.log('User upgraded to:', newPlan);
    store.update({ plan: newPlan });
  };

  return html\`
    <div>
      \${loadRemote({
        url: 'https://cdn.example.com/widgets/billing.js',
        props: {
          plan: store().plan,
          onUpgrade: handleUpgrade,
        },
      })}
    </div>
  \`;
}

// Remote
export default function BillingWidget(props: {
  plan: string;
  onUpgrade: (plan: string) => void;
}): Node {
  return html\`
    <div>
      <p>Current: \${props.plan}</p>
      <button onclick=\${() => props.onUpgrade('enterprise')}>
        Upgrade to Enterprise
      </button>
    </div>
  \`;
}`)}

      <h2>Iframe Communication (postMessage)</h2>
      <p>When using <code>isolation: 'iframe'</code>, props and callbacks aren't available directly. Use <code>postMessage</code>:</p>
      ${CodeBlock(`// Host — sending data to iframe remote
window.addEventListener('message', (event) => {
  if (event.origin !== 'https://cdn.example.com') return;

  if (event.data.type === 'UPGRADE_REQUEST') {
    store.update({ plan: event.data.plan });
  }
});

// Remote (inside iframe) — sending data to host
window.parent.postMessage(
  { type: 'UPGRADE_REQUEST', plan: 'enterprise' },
  'https://host-app.example.com'
);`)}

      ${Callout('Always validate event.origin in postMessage handlers. Never trust messages from unknown origins.', 'warn')}

      <h2>Communication Summary</h2>
      <table>
        <tr><th>Isolation</th><th>Host → Remote</th><th>Remote → Host</th></tr>
        <tr><td><code>none</code></td><td>Props (direct)</td><td>Callbacks (direct)</td></tr>
        <tr><td><code>shadow</code></td><td>Props (direct)</td><td>Callbacks (direct)</td></tr>
        <tr><td><code>iframe</code></td><td>postMessage</td><td>postMessage</td></tr>
      </table>
    </div>
  `;
}
